import type { WidgetField } from "@/domains/buildings/types/buildingWidget";
import { stripTrailingUnit } from "./stripTrailingUnit";

type SaveTarget = Pick<WidgetField, "state" | "value" | "unit" | "proposedChange">;

/**
 * True when writing `nextValue` would record an edit that changes nothing.
 *
 * `data.user_data` is append-only and doubles as the history table — both
 * `get_user_data_history` (the attribute panel) and `get_building_data_history`
 * (the building-wide modal) read its rows straight through, and
 * `buildHistoryTimeline` does not collapse consecutive identical values. So an
 * unconditional re-save of the value already on screen shows up as a real edit
 * by a real person at a real time, which is the bug this guards.
 *
 * The rule is deliberately narrow, because two flows depend on writing a value
 * identical to the one being displayed and must keep working:
 *
 *  - confirming a **pending** field — the value does not move, but `pending →
 *    confirmed` does, and that transition IS the edit;
 *  - **"Keep current value"** against a newer extractor finding, which
 *    re-stamps the row so the proposal stops resurfacing (WHA-3291/3643).
 *    A live `proposedChange` therefore always writes.
 *
 * Known limit: a multi-select re-saved in a different order is a different
 * string and still writes. `multiSelectCommit.ts` guards that path on the
 * selection itself, which is the right place for it.
 */
export function isRedundantUserSave(field: SaveTarget, nextValue: string | null): boolean {
  // A rejection (SQL NULL) is never redundant — it is how a value is cleared.
  if (nextValue === null) return false;
  if (field.state !== "confirmed") return false;
  if (field.proposedChange) return false;
  return normalize(nextValue, field.unit) === normalize(field.value, field.unit);
}

function normalize(value: string, unit: string | null | undefined): string {
  return (unit ? stripTrailingUnit(value) : value).trim();
}
